<template>
  <MainLayout>
    <PageHeader
      :title="t('app.views.cashRegister.title') || 'Cash Register'"
    />
    
    <CashRegisterTabs
      v-model="activeTab"
      class="mt-3"
    />

    <!-- Current Session Tab -->
    <div
      v-if="activeTab === 'current'"
      class="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start"
    >
      <div ref="sessionPanelRef">
        <CurrentSessionPanel
          :current-session="currentSession"
          :is-refreshing="isRefreshing"
          :session-duration="sessionDuration"
          :current-balance="currentBalance"
          :session-sales="sessionSales"
          :sales-count="salesCount"
          :session-expenses="sessionExpenses"
          :expenses-count="expensesCount"
          @open-open="openOpenModal"
          @open-close="() => openCloseModalWithCalculation(realCashBalance, openCloseModal)"
          @open-expense="openExpenseModal"
          @open-cut="openCutModal"
        />
      </div>

      <!-- Transactions List Component -->
      <TransactionsList
        :transactions="transactions"
        :style="transactionsPanelStyle"
        @add-expense="openExpenseModal"
      />

      <CutsHistoryList
        v-if="currentSession"
        :session-id="currentSession.id"
        class="xl:col-span-2"
      />
    </div>

    <!-- Reports Tab -->
    <div v-if="activeTab === 'reports'">
      <ReportsView />
    </div>

    <!-- Modals -->
    <OpenSessionModal
      :is-open="openModalOpen"
      :initial-balance="initialBalance"
      @close="closeModals"
      @save="handleOpenSession"
    />

    
    <CloseSessionModal
      :is-open="closeModalOpen"
      :actual-balance="actualBalance"
      :notes="closeNotes"
      :use-denominations="useDenominationCounting"
      :denominations="denominations"
      :real-cash-balance="realCashBalance"
      :initial-balance="currentSession?.initial_balance"
      :report="cutReport"
      :cash="cashPayments"
      :card="cardPayments"
      :digital="digitalPayments"
      :other="otherPayments"
      :custom-payments="customPayments"
      @close="closeModals"
      @save="handleCloseSession"
    />

    
    <ExpenseModal
      :is-open="expenseModalOpen"
      :amount="expenseAmount"
      :description="expenseDescription"
      @close="closeModals"
      @save="handleAddExpense"
    />

    
    <CutModal
      :is-open="cutModalOpen"
      :report="cutReport"
      :cash="cashPayments"
      :card="cardPayments"
      :digital="digitalPayments"
      :other="otherPayments"
      :custom-payments="customPayments"
      :initial-balance="currentSession?.initial_balance"
      @close="closeModals"
      @save="handlePerformCut"
    />
  </MainLayout>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import MainLayout from '@/components/layout/MainLayout.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import { useCashRegisterSession } from '@/composables/useCashRegisterSession';
import { useCashRegisterModals } from '@/composables/useCashRegisterModals';
import { useCashRegisterHandlers } from '@/composables/useCashRegisterHandlers';
import ReportsView from '@/components/ReportsView.vue';
import TransactionsList from '@/components/cashRegister/TransactionsList.vue';
import OpenSessionModal from '@/components/cashRegister/OpenSessionModal.vue';
import CloseSessionModal from '@/components/cashRegister/CloseSessionModal.vue';
import ExpenseModal from '@/components/cashRegister/ExpenseModal.vue';
import CutModal from '@/components/cashRegister/CutModal.vue';
import CutsHistoryList from '@/components/cashRegister/CutsHistoryList.vue';
import CashRegisterTabs from '@/components/cashRegister/CashRegisterTabs.vue';
import CurrentSessionPanel from '@/components/cashRegister/CurrentSessionPanel.vue';

/**
 * CashRegisterView - Main view for cash register management
 * 
 * Orchestrates cash register operations including:
 * - Session management (open/close)
 * - Transaction tracking
 * - Expense recording
 * - Cut operations
 * - Reports viewing
 * 
 * Uses composables for business logic separation following SOLID principles.
 */

const { t } = useI18n();

// Session management composable
const {
  currentSession,
  transactions,
  isRefreshing,
  currentBalance,
  realCashBalance,
  sessionExpenses,
  sessionSales,
  salesCount,
  expensesCount,
  sessionDuration,
  openSession: openSessionService,
  closeSession: closeSessionService,
  addExpense: addExpenseService,
  performCut: performCutService
} = useCashRegisterSession();

// Modal state management composable
const {
  openModalOpen,
  closeModalOpen,
  cutModalOpen,
  expenseModalOpen,
  initialBalance,
  actualBalance,
  closeNotes,
  cashPayments,
  cardPayments,
  digitalPayments,
  otherPayments,
  customPayments,
  expenseAmount,
  expenseDescription,
  useDenominationCounting,
  denominations,
  cutReport,
  openOpenModal,
  openCloseModal,
  openExpenseModal,
  openCutModal: openCutModalBase,
  closeModals,
  setCutReport,
  setPaymentBreakdown
} = useCashRegisterModals();

// Modal handlers composable
const {
  openCutModal,
  openCloseModalWithCalculation,
  handleOpenSession,
  handleCloseSession,
  handleAddExpense,
  handlePerformCut
} = useCashRegisterHandlers(
  currentSession,
  openSessionService,
  closeSessionService,
  addExpenseService,
  performCutService,
  closeModals,
  setCutReport,
  setPaymentBreakdown,
  openCutModalBase
);

// Local UI state
const activeTab = ref<'current' | 'reports'>('current');

const sessionPanelRef = ref<HTMLElement | null>(null);
const sessionPanelHeight = ref(0);
const isLargeScreen = ref(false);

let sessionPanelResizeObserver: ResizeObserver | null = null;

const updateScreenSize = () => {
  isLargeScreen.value = window.innerWidth >= 1024;
};

const measureSessionPanelHeight = () => {
  sessionPanelHeight.value = sessionPanelRef.value?.offsetHeight ?? 0;
};

const bindSessionPanelObserver = () => {
  sessionPanelResizeObserver?.disconnect();
  sessionPanelResizeObserver = null;

  if (!sessionPanelRef.value) return;

  sessionPanelResizeObserver = new ResizeObserver(() => {
    measureSessionPanelHeight();
  });
  sessionPanelResizeObserver.observe(sessionPanelRef.value);
};

const transactionsPanelStyle = computed(() => {
  if (!isLargeScreen.value || sessionPanelHeight.value <= 0) return undefined;
  return { height: `${sessionPanelHeight.value}px` };
});

onMounted(() => {
  updateScreenSize();

  window.addEventListener('resize', updateScreenSize);
  window.addEventListener('resize', measureSessionPanelHeight);

  nextTick(() => {
    measureSessionPanelHeight();
    bindSessionPanelObserver();
  });
});

watch(
  activeTab,
  async (tab) => {
    if (tab !== 'current') return;
    await nextTick();
    measureSessionPanelHeight();
    bindSessionPanelObserver();
  }
);

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize);
  window.removeEventListener('resize', measureSessionPanelHeight);
  sessionPanelResizeObserver?.disconnect();
});
</script>
